import React, { useRef, useState } from 'react';
import Tippy from '@tippyjs/react/headless';
import './tooltip.scss';

type Orientation = 'top' | 'bottom' | 'left' | 'right';

type Props = {
  children: React.ReactNode;
  content: React.ReactNode;
  isMobile: boolean;
  showOnClick?: boolean;
  onOpenEvent?: () => void;
  orientation?: Orientation;
  showOnHover?: boolean;
  lightMode?: boolean;
  copyToClipboard?: boolean;
};

type VariableProps = {
  visible?: boolean;
  onClickOutside?: () => void;
};

const Tooltip = ({
  children,
  content,
  isMobile,
  onOpenEvent,
  showOnClick = false,
  orientation = 'top',
  showOnHover = false,
  lightMode = false,
  copyToClipboard = false,
}: Props) => {
  const arrowRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const mobileOffset = 30;
  const desktopOffset = 20;

  const getOffset = (orientation: Orientation, isOffsetForMobile: boolean): [number, number] => {
    const offset = isOffsetForMobile ? mobileOffset : desktopOffset;

    switch (orientation) {
      case 'top':
        return [0, offset];
      case 'bottom':
        return [0, offset];
      case 'left':
        return [offset, 0];
      case 'right':
        return [offset, 0];
      default:
        return [0, 0];
    }
  };

  const getTippy = (showOnClick: boolean, isMobile: boolean) => {
    const variableProps: VariableProps = {};

    if (showOnClick || showOnHover) {
      variableProps.visible = visible;
      variableProps.onClickOutside = () => setVisible(false);
    }

    return (
      <Tippy
        {...variableProps}
        placement={orientation}
        render={(attrs) => (
          <div
            className={`Tooltip ${lightMode ? 'light-mode' : ''} ${
              copyToClipboard ? 'copy-to-clipboard' : ''
            }`}
            tabIndex={-1}
            {...attrs}
          >
            <p className="tooltip-text">{content}</p>
            <div
              id="arrow"
              data-popper-arrow=""
              className={`arrow ${isMobile ? 'is-mobile' : ''}`}
              ref={arrowRef}
            />
          </div>
        )}
        offset={getOffset(orientation, isMobile)}
        popperOptions={{
          modifiers: [
            {
              name: 'arrow',
              options: {
                element: arrowRef.current,
              },
            },
          ],
        }}
      >
        <button
          type="button"
          onMouseEnter={() => {
            if (showOnHover) {
              setVisible(true);
              !!onOpenEvent && onOpenEvent();
            }
          }}
          onMouseLeave={() => {
            setVisible(false);
          }}
          onClick={() => {
            if (showOnClick) {
              setVisible(true);
              !!onOpenEvent && onOpenEvent();
            }
          }}
          className="tippy-child-container"
        >
          {children}
        </button>
      </Tippy>
    );
  };

  return getTippy(showOnClick, isMobile);
};

export default Tooltip;
